import type {
  AlarmState,
  BypassState,
  PgmItem,
  PgmState,
  SirenState,
  TroubleState,
} from "./api";

export interface CommandSnapshot {
  alarm: AlarmState;
  bypass: BypassState;
  pgms: PgmState;
  siren: SirenState;
  troubles: TroubleState;
}

export type CommandPostcondition = (snapshot: CommandSnapshot) => boolean;

export function resolveConfirmedSnapshot<
  TSnapshot extends CommandSnapshot,
  TConfirmed,
>(
  current: TSnapshot,
  confirmed: TConfirmed,
  applyConfirmedState: (snapshot: TSnapshot, state: TConfirmed) => TSnapshot,
  postcondition: CommandPostcondition,
): TSnapshot | null {
  const next = applyConfirmedState(current, confirmed);
  return postcondition(next) ? next : null;
}

function versionAdvanced(current: number, previous: number): boolean {
  return Number.isInteger(current) && current > previous;
}

export function areaPostcondition(
  area: AlarmState["areas"][number]["area"],
  targetState: AlarmState["areas"][number]["state"],
  previousVersion: number,
): CommandPostcondition {
  return (snapshot) => {
    const current = snapshot.alarm.areas.find((item) => item.area === area);
    return versionAdvanced(snapshot.alarm.state_version, previousVersion)
      && current?.state === targetState;
  };
}

export function allAreasPostcondition(
  targetState: AlarmState["areas"][number]["state"],
  previousVersion: number,
): CommandPostcondition {
  const expectedGlobal = targetState === "ARMED" ? "ARMED_ALL" : "DISARMED_ALL";
  return (snapshot) => versionAdvanced(snapshot.alarm.state_version, previousVersion)
    && snapshot.alarm.global_state === expectedGlobal
    && snapshot.alarm.areas.length > 0
    && snapshot.alarm.areas.every((area) => area.state === targetState);
}

export function bypassPostcondition(
  zone: number,
  targetActive: boolean,
  previousVersion: number,
): CommandPostcondition {
  return (snapshot) => {
    const active = snapshot.bypass.active.find((item) => item.zone === zone);
    const row = [...(snapshot.bypass.zones ?? []), ...snapshot.bypass.active]
      .filter((item) => item.zone === zone)
      .sort((left, right) => right.bypass_version - left.bypass_version)[0];
    if (!row || !versionAdvanced(row.bypass_version, previousVersion)) return false;
    return targetActive
      ? active?.status === "ACTIVE"
      : !active && row.status === "RESTORED";
  };
}

export function pgmPostcondition(
  pgmId: PgmItem["pgm_id"],
  targetState: PgmItem["state"],
  previousVersion: number,
): CommandPostcondition {
  return (snapshot) => {
    const current = snapshot.pgms.pgms.find((item) => item.pgm_id === pgmId);
    return Boolean(current
      && versionAdvanced(current.pgm_version, previousVersion)
      && current.state === targetState);
  };
}

export function sirenPostcondition(
  targetState: SirenState["siren"]["state"],
  previousVersion: number,
): CommandPostcondition {
  return (snapshot) => versionAdvanced(
    snapshot.siren.siren.siren_version,
    previousVersion,
  ) && snapshot.siren.siren.state === targetState;
}

export function troubleAcknowledgedPostcondition(troubleId: string): CommandPostcondition {
  return (snapshot) => {
    const current = snapshot.troubles.active.find((item) => item.trouble_id === troubleId);
    return current?.status === "ACKNOWLEDGED";
  };
}
